<template>
  <RfSelectFieldShell
    ref="shellEl"
    :title="title"
    :requiredTitle="requiredTitle"
    :variant="wrapperVariant"
    :size="size"
    autoWidth
    :errorMessage="errorMessage"
    :hintMessage="hintMessage"
    :disabled="disabled"
    :hideIcon="hideIcon"
    :sameWidth="sameWidth"
    :maxHeight="maxHeight"
    @show="onShow"
    @hide="onHide"
  >
    <template v-if="slots.title" #title>
      <slot name="title" />
    </template>

    <slot v-if="slots.activator" name="activator" :opened="!!shellEl?.opened" />
    <template v-else-if="tableStyle">
      <span class="text-body text-black-1">{{ activator }}</span>
      <RfFilterIcon
        v-if="!disabled"
        :class="{ 'fill-orange-1!': explicit && hasSelectedElements }"
        class="fill-beige-50 h-4! w-4! shrink-0"
      />
    </template>
    <template v-else>
      <span class="inline-flex h-6 items-center gap-1">
        <RfFilterAlterIcon v-if="hideIcon" class="h-6 w-6" />
        <span class="text-body text-black-1">{{ activator }}</span>
      </span>
      <span
        v-if="extended"
        class="text-beige-50 inline-flex items-center"
        :class="disabled ? 'pr-2' : 'pr-1'"
      >
        <template v-if="selectedValuesAll.length === 0"> All </template>
        <span v-else class="text-beige-90 inline-flex items-center">
          <RfColorTag
            v-for="(value, index) in selectedValuesAll.slice(0, visibleCount)"
            :key="index"
            size="small"
            pill
            backgroundColor="#EDEBE4"
            disabled
          >
            {{ value }}
          </RfColorTag>
          <RfColorTag
            v-if="selectedValuesAll.length > visibleCount"
            size="small"
            pill
            backgroundColor="#EDEBE4"
            disabled
          >
            +{{ selectedValuesAll.length - visibleCount }}
          </RfColorTag>
        </span>
      </span>
    </template>

    <template #menu>
      <div v-if="search" class="w-full px-3 pt-3">
        <input
          v-model="searchValue"
          :aria-label="typeof search === 'string' ? search : 'Search'"
          :placeholder="typeof search === 'string' ? search : 'Search...'"
          class="bg-goal w-full rounded p-3 disabled:opacity-50"
          :disabled="isLoading"
        />
      </div>

      <div
        class="rf-filter-field--wrapper flex w-full max-w-full shrink grow-0 flex-col items-start gap-4 overflow-y-auto p-4 pr-3"
      >
        <template v-if="isLoading">
          <RfBaseSkeleton v-for="i in 5" :key="i" width="240" height="24" class="my-1 w-full" />
        </template>
        <template v-else-if="normalizedGroups.length">
          <slot v-if="slots.prepend" name="prepend"></slot>

          <template v-for="(group, key) in normalizedGroups" :key="key">
            <template v-if="group.el?.length">
              <template v-if="isMultipleGroups">
                <hr :key="key" class="border-strokes h-0 w-full border-t first-of-type:hidden" />
                <button
                  :key="key"
                  class="flex w-full items-center justify-between self-start text-left"
                  :aria-expanded="!collapsed[key]"
                  :aria-controls="`rf-filter-field-group-${uid}-${key}`"
                  @click="collapsed = { ...collapsed, [key]: !collapsed[key] }"
                >
                  {{ group.name }}
                  <Transition mode="out-in">
                    <RfChevronMiniClosedIcon v-if="collapsed[key]" class="h-7! w-7!" />
                    <RfChevronMiniOpenIcon v-else class="h-7! w-7!" />
                  </Transition>
                </button>
              </template>
              <div
                :id="`rf-filter-field-group-${uid}-${key}`"
                :key="key"
                class="flex w-full shrink grow-0 flex-col items-start justify-stretch gap-4"
                :class="{ '-mt-4 h-0 overflow-hidden': collapsed[key] }"
              >
                <RfCheckbox
                  v-for="el in group.el"
                  :key="el.key"
                  class="rf-row--checkbox w-full text-sm!"
                  size="small"
                  :class="{
                    'rf-row--checkbox-col-selected': isGroupItemSelected(key, el),
                    'pl-5': isMultipleGroups,
                  }"
                  :data-cy="`table-filter--${el.key}`"
                  :modelValue="isGroupItemSelected(key, el)"
                  @update:modelValue="toggleGroupItem(key, el)"
                >
                  <slot v-if="slots.item" name="item" :el="el" :value="el.value" />
                  <template v-else>{{ el.value }}</template>
                </RfCheckbox>
              </div>
            </template>
          </template>
        </template>
        <template v-else>
          <div class="text-grey-3">No results found</div>
        </template>
      </div>

      <div class="mb-2 flex w-full min-w-36">
        <span v-if="search && localSelectedCount > 0" class="text-grey-3 pt-2 pl-4">
          {{ localSelectedCount }} selected
          <span v-if="searchedLocalSelectedCount !== localSelectedCount">
            ({{ localSelectedCount - searchedLocalSelectedCount }} hidden)
          </span>
        </span>

        <RfButtonNew
          class="mr-4 ml-auto pr-2! pl-5! [&_.rf-button--container]:font-semibold!"
          size="link-sm"
          type="link"
          data-cy="table-filter--apply"
          @click="applySelection"
        >
          Apply
        </RfButtonNew>
      </div>
    </template>
  </RfSelectFieldShell>
</template>

<script setup>
import { ref, useSlots, computed, getCurrentInstance } from "vue";
import RfChevronMiniClosedIcon from "@/components/icons/RfChevronMiniClosedIcon.vue";
import RfChevronMiniOpenIcon from "@/components/icons/RfChevronMiniOpenIcon.vue";
import RfFilterAlterIcon from "@/components/icons/RfFilterAlterIcon.vue";
import RfFilterIcon from "@/components/icons/RfFilterIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfSelectFieldShell from "@/components/inputs/RfSelectFieldShell.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { uniqueBy } from "@/utils/arrayUtils";

const { uid } = getCurrentInstance();
const slots = useSlots();
const emit = defineEmits(["show", "hide", "selected"]);

const props = defineProps({
  title: { type: String, default: "" },
  requiredTitle: { type: Boolean, default: false },
  variant: { type: String, default: null },
  size: { type: String, default: "base" },
  sameWidth: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  hintMessage: { type: String, default: "" },
  disabled: { type: [Boolean, String], default: false },
  hideIcon: { type: Boolean, default: false },
  search: { type: [Boolean, String], default: false },

  groups: { type: Object, default: () => ({}) },
  selected: { type: Object, default: () => ({}) },
  activator: { type: String, default: "Filter" },
  extended: { type: Boolean, default: false },
  visibleCount: { type: [Number, String], default: 1 },
  tableStyle: { type: Boolean, default: false },
  explicit: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  maxHeight: { type: String, default: "400px" },
});

// RfMenu/RfFilter's old vocabulary had a borderless "plain" variant; RfInputWrapper only
// understands "default"/"outlined" — translate, and default to the bordered pill look.
const wrapperVariant = computed(() => {
  const variant = props.variant ?? (props.tableStyle ? "plain" : "outlined");
  return variant === "plain" ? "default" : variant;
});

const hasSelectedElements = computed(() =>
  Object.entries(props.selected || {})
    .filter(([key]) => props.groups?.[key])
    .some(([_key, filter]) => Object.keys(filter).length > 0),
);
const visibleCount = computed(() => Number(props.visibleCount));

const shellEl = ref();
const searchValue = ref("");
const localSelected = ref({});
const collapsed = ref({});

const isMultipleGroups = computed(() => Object.keys(props.groups || {}).length > 1);

const normalizeGroupEl = ({ el, key, value }) => {
  const elArr = Array.isArray(el) ? el : Object.values(el);
  return elArr.map(obj => ({
    ...obj,
    key: obj[key],
    value: typeof value === "function" ? value(obj) : obj[value],
  }));
};

const searchFilter = value =>
  value && searchValue.value ? value.toLowerCase().includes(searchValue.value.toLowerCase()) : true;

const normalizedGroups = computed(() => {
  const result = { length: 0 };
  for (const [key, group] of Object.entries(props.groups || {})) {
    const groupEl = Array.isArray(group.el) ? group.el : Object.values(group.el);
    const el = uniqueBy(
      normalizeGroupEl({ ...group, el: groupEl }).filter(({ value }) => searchFilter(value)),
      "value",
    );
    result.length += el.length;
    result[key] = { ...group, el };
  }
  return result;
});

const localSelectedCount = computed(() =>
  Object.values(localSelected.value).reduce((sum, group) => sum + Object.values(group).length, 0),
);
const searchedLocalSelectedCount = computed(() =>
  Object.values(localSelected.value).reduce(
    (sum, group) => sum + Object.values(group).filter(searchFilter).length,
    0,
  ),
);

const isGroupItemSelected = (key, el) => !!localSelected.value[key]?.[el.key];
const toggleGroupItem = (key, el) => {
  const group = { ...localSelected.value[key] };
  if (isGroupItemSelected(key, el)) delete group[el.key];
  else group[el.key] = el.value;
  localSelected.value = { ...localSelected.value, [key]: group };
};

const resetDraft = () => {
  const { groups, selected } = props;
  localSelected.value = Object.keys(groups || {}).reduce(
    (result, key) => ({ ...result, [key]: selected[key] ?? {} }),
    {},
  );
};

const selectedValuesAll = computed(() =>
  Object.keys(props.groups || {})
    .map(key => Object.values(props.selected?.[key] || {}))
    .flat(),
);

const onShow = () => {
  resetDraft();
  emit("show");
};
const onHide = () => {
  searchValue.value = "";
  emit("hide");
};

const applySelection = () => {
  emit("selected", localSelected.value);
  shellEl.value?.hide?.();
};

defineExpose({ opened: computed(() => shellEl.value?.opened) });
</script>
